import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import { Organisation, StripeSubscription } from '../types';
import { Invoice } from '../shared';
import { Link } from 'react-router-dom';
import {
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  EyeIcon,
  CreditCardIcon,
  DocumentTextIcon,
  CalendarIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArchiveBoxIcon,
  ArchiveBoxArrowDownIcon,
} from '@heroicons/react/24/outline';
import { ErrorBanner } from './ErrorBanner';

interface OrganisationDetailModalProps {
  organisation: Organisation | null;
  isOpen: boolean;
  onClose: () => void;
  /** Called after archive/restore so the parent can refresh its list. */
  onArchiveStateChange?: (updated: Organisation) => void;
}

const getCurrencySymbol = (currency: string): string => {
  const symbols = {
    GBP: '£',
    USD: '$',
    EUR: '€',
    gbp: '£',
    usd: '$',
    eur: '€',
  };
  return symbols[currency as keyof typeof symbols] || currency;
};

const formatAmount = (amount: number | null | undefined, currency: string = 'GBP'): string => {
  if (amount === null || amount === undefined) return 'N/A';
  return `${getCurrencySymbol(currency)}${(amount / 100).toFixed(2)}`;
};

const formatDate = (timestamp: number | string | Date): string => {
  return new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp).toLocaleDateString(
    'en-GB',
    {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }
  );
};

const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'active':
    case 'paid':
    case 'trialing':
      return 'text-green-600 bg-green-100';
    case 'pending':
    case 'sent':
      return 'text-yellow-600 bg-yellow-100';
    case 'past_due':
    case 'overdue':
    case 'unpaid':
    case 'suspended':
      return 'text-red-600 bg-red-100';
    case 'canceled':
    case 'cancelled':
    case 'void':
      return 'text-base-content/70 bg-gray-100';
    case 'incomplete':
    case 'draft':
      return 'text-blue-600 bg-blue-100';
    default:
      return 'text-base-content/70 bg-gray-100';
  }
};

const getStatusIcon = (status: string): React.ReactNode => {
  switch (status.toLowerCase()) {
    case 'active':
    case 'paid':
    case 'trialing':
      return <CheckCircleIcon className="h-4 w-4" />;
    case 'pending':
    case 'sent':
      return <ClockIcon className="h-4 w-4" />;
    case 'past_due':
    case 'overdue':
    case 'unpaid':
    case 'suspended':
      return <ExclamationCircleIcon className="h-4 w-4" />;
    case 'canceled':
    case 'cancelled':
    case 'void':
      return <XCircleIcon className="h-4 w-4" />;
    default:
      return <DocumentTextIcon className="h-4 w-4" />;
  }
};

export const OrganisationDetailModal: React.FC<OrganisationDetailModalProps> = ({
  organisation,
  isOpen,
  onClose,
  onArchiveStateChange,
}) => {
  const [subscription, setSubscription] = useState<StripeSubscription | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'subscription' | 'invoices'>('overview');
  const [archivePromptOpen, setArchivePromptOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveBusy, setArchiveBusy] = useState(false);
  const loadOrganisationDetails = useCallback(async () => {
    if (!organisation) return;

    setLoading(true);
    setError(null);

    try {
      // Load subscription data if organization has a subscription
      if (organisation.stripeSubscriptionId) {
        try {
          const subscriptionResponse = await api.getSubscription(organisation.id);
          setSubscription(subscriptionResponse.data);
        } catch (subError) {
          console.warn('Failed to load subscription:', subError);
          // Don't set error for subscription failure, just log it
        }
      }

      // Load invoices if organization has a Stripe customer ID
      if (organisation.stripeCustomerId) {
        try {
          const invoicesData = await api.getInvoices(organisation.id);
          // Ensure invoicesData is always an array
          if (Array.isArray(invoicesData)) {
            setInvoices(invoicesData);
          } else {
            console.warn('Invoices data is not an array:', invoicesData);
            setInvoices([]);
          }
        } catch (invError) {
          console.warn('Failed to load invoices:', invError);
          setInvoices([]); // Set empty array on error
        }
      }
    } catch (err) {
      console.error('Error loading organisation details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load organisation details');
    } finally {
      setLoading(false);
    }
  }, [organisation]);

  useEffect(() => {
    if (isOpen && organisation) {
      loadOrganisationDetails();
    }
  }, [isOpen, organisation, loadOrganisationDetails]);

  const handleClose = () => {
    onClose();
    setSubscription(null);
    setInvoices([]);
    setError(null);
    setActiveTab('overview');
    setArchivePromptOpen(false);
    setArchiveReason('');
  };

  const handleArchive = async () => {
    if (!organisation) return;
    setArchiveBusy(true);
    setError(null);
    try {
      const updated = await api.archiveOrganisation(
        organisation.id,
        archiveReason.trim() || undefined
      );
      setArchivePromptOpen(false);
      setArchiveReason('');
      onArchiveStateChange?.(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive organisation');
    } finally {
      setArchiveBusy(false);
    }
  };

  const handleRestore = async () => {
    if (!organisation) return;
    setArchiveBusy(true);
    setError(null);
    try {
      const updated = await api.restoreOrganisation(organisation.id);
      onArchiveStateChange?.(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restore organisation');
    } finally {
      setArchiveBusy(false);
    }
  };

  if (!isOpen || !organisation) return null;

  const isArchived = Boolean(organisation.archivedAt);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-base-100 rounded-lg shadow-xl max-w-6xl w-full h-[85vh] min-h-[700px] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-base-300">
          <div className="flex items-center space-x-3">
            <EyeIcon className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-base-content">{organisation.name}</h2>
              <p className="text-sm text-base-content/70">Organization Details</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/orgs/${organisation.id}`}
              onClick={handleClose}
              className="btn btn-ghost btn-sm gap-1"
              title="Open full page"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              <span>Open page</span>
            </Link>
            <button
              onClick={handleClose}
              className="p-2 text-base-content/50 hover:text-base-content/70 rounded-full hover:bg-base-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-base-300">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('subscription')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-1 ${
                activeTab === 'subscription'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
              }`}
            >
              <CreditCardIcon className="h-4 w-4" />
              <span>Subscription</span>
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-1 ${
                activeTab === 'invoices'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
              }`}
            >
              <DocumentTextIcon className="h-4 w-4" />
              <span>Invoices ({Array.isArray(invoices) ? invoices.length : 0})</span>
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          {loading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2 text-base-content/70">Loading details...</span>
            </div>
          )}

          <ErrorBanner error={error} onClose={() => setError(null)} autoScroll={true} />

          {/* Overview Tab */}
          {activeTab === 'overview' && !loading && (
            <div className="space-y-6">
              {isArchived && (
                <div className="border border-yellow-300 bg-yellow-50 rounded-lg p-4 flex items-start space-x-3">
                  <ArchiveBoxIcon className="h-6 w-6 text-yellow-700 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-yellow-900">Archived</h3>
                    <p className="text-sm text-yellow-800 mt-1">
                      Archived on{' '}
                      {organisation.archivedAt ? formatDate(organisation.archivedAt) : 'unknown'}
                      {organisation.archivedBy ? ` by ${organisation.archivedBy}` : ''}.
                    </p>
                    {organisation.archiveReason && (
                      <p className="text-sm text-yellow-800 mt-1">
                        <span className="font-medium">Reason:</span> {organisation.archiveReason}
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-base-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-base-content mb-4">Basic Information</h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-base-content/70">Name</dt>
                      <dd className="text-sm text-base-content">{organisation.name}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-base-content/70">Billing Email</dt>
                      <dd className="text-sm text-base-content">{organisation.billingEmail}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-base-content/70">Status</dt>
                      <dd>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            organisation.enabled
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {organisation.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </dd>
                    </div>
                  </dl>
                </div>

                <div className="bg-base-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-base-content mb-4">Plan & Usage</h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-base-content/70">Plan ID</dt>
                      <dd className="text-sm text-base-content">{organisation.planId}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-base-content/70">Projects</dt>
                      <dd className="text-sm text-base-content">
                        {organisation.currentProjects || 0} /{' '}
                        {organisation.maxProjects || 'Unlimited'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-base-content/70">Billing Method</dt>
                      <dd>
                        <span
                          className={`inline-flex px-2 py-1 text-xs rounded-full ${
                            organisation.billingMethod === 'automated'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}
                        >
                          {organisation.billingMethod === 'automated' ? 'Automated' : 'Invoice/PO'}
                        </span>
                      </dd>
                    </div>
                    {organisation.planStatus && (
                      <div>
                        <dt className="text-sm font-medium text-base-content/70">Plan Status</dt>
                        <dd>
                          <span
                            className={`inline-flex px-2 py-1 text-xs rounded-full ${getStatusColor(
                              organisation.planStatus
                            )}`}
                          >
                            {organisation.planStatus.charAt(0).toUpperCase() +
                              organisation.planStatus.slice(1)}
                          </span>
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>

              <div className="bg-base-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-base-content mb-4">Stripe Integration</h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-base-content/70">Customer ID</dt>
                    <dd className="text-sm text-base-content font-mono">
                      {organisation.stripeCustomerId || 'Not connected'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-base-content/70">Subscription ID</dt>
                    <dd className="text-sm text-base-content font-mono">
                      {organisation.stripeSubscriptionId || 'No subscription'}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="bg-base-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-base-content mb-4">Timestamps</h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-base-content/70">Created</dt>
                    <dd className="text-sm text-base-content">
                      {formatDate(organisation.createdAt)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-base-content/70">Updated</dt>
                    <dd className="text-sm text-base-content">
                      {formatDate(organisation.updatedAt)}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && !loading && (
            <div className="space-y-6">
              {!organisation.stripeSubscriptionId ? (
                <div className="text-center py-8">
                  <CreditCardIcon className="h-12 w-12 text-base-content/50 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-base-content mb-2">No Subscription</h3>
                  <p className="text-base-content/70">
                    This organization does not have an active subscription.
                  </p>
                </div>
              ) : subscription ? (
                <div className="space-y-4">
                  <div className="bg-base-100 border border-base-300 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-base-content">
                        Subscription Details
                      </h3>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(subscription.status)}
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            subscription.status
                          )}`}
                        >
                          {subscription.status.charAt(0).toUpperCase() +
                            subscription.status.slice(1)}
                        </span>
                      </div>
                    </div>

                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <dt className="text-sm font-medium text-base-content/70">
                          Subscription ID
                        </dt>
                        <dd className="text-sm text-base-content font-mono">{subscription.id}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-base-content/70">Current Period</dt>
                        <dd className="text-sm text-base-content">
                          {formatDate(subscription.current_period_start)} -{' '}
                          {formatDate(subscription.current_period_end)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-base-content/70">
                          Cancel at Period End
                        </dt>
                        <dd className="text-sm text-base-content">
                          {subscription.cancel_at_period_end ? 'Yes' : 'No'}
                        </dd>
                      </div>
                      {subscription.latest_invoice && (
                        <div>
                          <dt className="text-sm font-medium text-base-content/70">
                            Latest Invoice
                          </dt>
                          <dd className="text-sm text-base-content">
                            {formatAmount(subscription.latest_invoice.amount_due)} (
                            {subscription.latest_invoice.status})
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  {/* Subscription Items */}
                  {subscription.items.data && subscription.items.data.length > 0 && (
                    <div className="bg-base-100 border border-base-300 rounded-lg p-4">
                      <h4 className="text-md font-medium text-base-content mb-3">
                        Subscription Items
                      </h4>
                      <div className="space-y-3">
                        {subscription.items.data.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between p-3 bg-base-200 rounded-lg"
                          >
                            <div>
                              <p className="text-sm font-medium text-base-content">
                                {item.price.nickname || item.price.id}
                              </p>
                              <p className="text-sm text-base-content/70">
                                Quantity: {item.quantity}
                              </p>
                            </div>
                            {item.price.unit_amount && (
                              <div className="text-right">
                                <p className="text-sm font-medium text-base-content">
                                  {formatAmount(item.price.unit_amount, item.price.currency)}
                                  {item.price.recurring && `/${item.price.recurring.interval}`}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-base-content/70">Loading subscription details...</p>
                </div>
              )}
            </div>
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && !loading && (
            <div className="space-y-4">
              {!organisation.stripeCustomerId ? (
                <div className="text-center py-8">
                  <DocumentTextIcon className="h-12 w-12 text-base-content/50 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-base-content mb-2">No Stripe Customer</h3>
                  <p className="text-base-content/70">
                    This organization is not connected to Stripe.
                  </p>
                </div>
              ) : !Array.isArray(invoices) || invoices.length === 0 ? (
                <div className="text-center py-8">
                  <DocumentTextIcon className="h-12 w-12 text-base-content/50 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-base-content mb-2">No Invoices</h3>
                  <p className="text-base-content/70">No invoices found for this organization.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {Array.isArray(invoices) &&
                    invoices.map((invoice) => (
                      <div
                        key={invoice.id}
                        className="bg-base-100 border border-base-300 rounded-lg p-4 hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h4 className="text-sm font-medium text-base-content">
                                Invoice #{invoice.invoiceNumber || invoice.id}
                              </h4>
                              <div className="flex items-center space-x-1">
                                {getStatusIcon(invoice.status)}
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                                    invoice.status
                                  )}`}
                                >
                                  {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                                </span>
                              </div>
                            </div>
                            <div className="mt-1 flex items-center space-x-4 text-sm text-base-content/70">
                              <div className="flex items-center space-x-1">
                                <CalendarIcon className="h-4 w-4" />
                                <span>
                                  {invoice.createdAt ? formatDate(invoice.createdAt) : 'N/A'}
                                </span>
                              </div>
                              {invoice.dueDate && (
                                <div className="flex items-center space-x-1">
                                  <ClockIcon className="h-4 w-4" />
                                  <span>Due: {formatDate(invoice.dueDate)}</span>
                                </div>
                              )}
                              {invoice.purchaseOrderNumber && (
                                <div>PO: {invoice.purchaseOrderNumber}</div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center space-x-1">
                              <BanknotesIcon className="h-4 w-4 text-base-content/50" />
                              <span className="text-lg font-semibold text-base-content">
                                {formatAmount(invoice.total, invoice.currency)}
                              </span>
                            </div>
                            {invoice.amountPaid !== undefined && invoice.amountPaid > 0 && (
                              <div className="text-sm text-green-600">
                                Paid: {formatAmount(invoice.amountPaid, invoice.currency)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action footer */}
        <div className="border-t border-base-300 bg-base-200 px-6 py-4">
          {archivePromptOpen ? (
            <div className="space-y-3">
              <label
                htmlFor="archiveReasonField"
                className="block text-sm font-medium text-base-content"
              >
                Archive reason (optional)
              </label>
              <textarea
                id="archiveReasonField"
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                rows={2}
                placeholder="e.g. Customer paused operations until next season"
                className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                disabled={archiveBusy}
              />
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setArchivePromptOpen(false);
                    setArchiveReason('');
                  }}
                  disabled={archiveBusy}
                  className="px-4 py-2 text-sm font-medium text-base-content bg-base-100 border border-base-300 rounded-md hover:bg-base-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleArchive}
                  disabled={archiveBusy}
                  className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700 disabled:opacity-50 inline-flex items-center space-x-2"
                >
                  <ArchiveBoxIcon className="h-4 w-4" />
                  <span>{archiveBusy ? 'Archiving…' : 'Confirm archive'}</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end">
              {isArchived ? (
                <button
                  type="button"
                  onClick={handleRestore}
                  disabled={archiveBusy}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 inline-flex items-center space-x-2"
                >
                  <ArchiveBoxArrowDownIcon className="h-4 w-4" />
                  <span>{archiveBusy ? 'Restoring…' : 'Restore organisation'}</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setArchivePromptOpen(true)}
                  className="px-4 py-2 text-sm font-medium text-yellow-900 bg-yellow-100 border border-yellow-300 rounded-md hover:bg-yellow-200 inline-flex items-center space-x-2"
                >
                  <ArchiveBoxIcon className="h-4 w-4" />
                  <span>Archive organisation</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
